import React from "react";
import { Link, NavLink, useNavigate } from "remix";
import brandIcon from "~/../public/android-chrome-96x96.png";
import { FullUser } from "~/new_shared/db/user.server";
import { TntAllUrls, WebBaseUrl } from "~/new_shared/urls";
import { Fragment } from "react";
import { Disclosure, Menu, Transition } from "@headlessui/react";
import { BellIcon, MenuIcon, XIcon, UserCircleIcon } from "@heroicons/react/outline";
import { Button, ButtonClasses } from ".";

interface Props {
  user: FullUser | undefined;
}

interface NavItemModel {
  name: string;
  href: string;
  current: boolean;
}

const SignedInNavItems = [
  {
    name: "Inbox",
    href: TntAllUrls.Inbox(),
    current: false /* for now, not using these highlights */,
  },
];
/*
  <Link to={TntAllUrls.Login()} className="px-3 no-underline text-slate-500">
    Sign in
  </Link>
  <Link to={TntAllUrls.SignUp()} className={classNames(ButtonClasses)}>
    Try for Free
  </Link>
</div> 
*/
const SignedOutNavItems = [
  { name: "How it works", href: TntAllUrls.HowItWorks(), current: false },
  { name: "FAQ", href: TntAllUrls.Faq(), current: false },
];
const userMenuItems = [
  { name: "Account settings", href: TntAllUrls.AccountSettings() },
  { name: "Sign out", href: TntAllUrls.Logout() },
];

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

const LeftHandItems = ({ isSignedIn }: { isSignedIn: boolean }) => {
  const nav = useNavigate();
  const menuItems = isSignedIn ? SignedInNavItems : SignedOutNavItems;
  return (
    <>
      <div
        className="flex-shrink-0 flex items-center cursor-pointer"
        onClick={() => nav(TntAllUrls.Root())}
      >
        <img src={brandIcon} width="30" height="30" className="inline align-text-bottom" />
        ThxNoThx
      </div>
      <div className="hidden sm:-my-px sm:ml-6 sm:flex sm:space-x-8">
        {menuItems.map((item) => (
          <a
            key={item.name}
            href={item.href}
            className={classNames(
              item.current
                ? "border-amber-500 text-gray-900"
                : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700",
              "inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium no-underline ",
            )}
            aria-current={item.current ? "page" : undefined}
          >
            {item.name}
          </a>
        ))}
      </div>
    </>
  );
};

const RightHandItems = ({ isSignedIn }: { isSignedIn: boolean }) => {
  if (!isSignedIn) {
    return (
      <div className="my-auto">
        {/* sign in should always be an absolute URL with full domain name, so don't use Link */}
        <a href={TntAllUrls.Login()} className="px-3 no-underline text-slate-500">
          Sign in
        </a>
        <Link to={TntAllUrls.SignUp()} className={classNames(ButtonClasses)}>
          Try for Free
        </Link>
      </div>
    );
  }
  return (
    <div className="hidden sm:ml-6 sm:flex sm:items-center">
      {/* notification icon would go here*/}

      {/* Profile dropdown */}
      <Menu as="div" className="ml-3 relative">
        <div>
          <Menu.Button className="max-w-xs bg-white flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500">
            <span className="sr-only">Open user menu</span>
            <UserCircleIcon className="h-6 w-6 block text-gray-500" />
          </Menu.Button>
        </div>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-200"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
            {userMenuItems.map((item) => (
              <Menu.Item key={item.name}>
                {({ active }) => (
                  <a
                    href={item.href}
                    className={classNames(
                      active ? "bg-gray-100" : "",
                      "block px-4 py-2 text-sm text-gray-700 no-underline",
                    )}
                  >
                    {item.name}
                  </a>
                )}
              </Menu.Item>
            ))}
          </Menu.Items>
        </Transition>
      </Menu>
      <div className="absolute inset-y-0 right-0 flex items-center pr-2 sm:static sm:inset-auto sm:ml-6 sm:pr-0"></div>
    </div>
  );
};

const MobileMenuOpenButton = ({ menuOpen }: { menuOpen: boolean }) => (
  <div className="-mr-2 flex items-center sm:hidden">
    {/* Mobile menu button */}
    <Disclosure.Button className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-amber-500">
      <span className="sr-only">Open main menu</span>
      {menuOpen ? (
        <XIcon className="block h-6 w-6" aria-hidden="true" />
      ) : (
        <MenuIcon className="block h-6 w-6" aria-hidden="true" />
      )}
    </Disclosure.Button>
  </div>
);

export const VeryBasicPage: React.FC = ({ children }) => {
  return (
    <div className="min-h-full">
      <div className="py-2 px-4 sm:px-6 lg:px-8 border-b border-gray-200">
        <Link to="/" className="no-underline text-black">
          <img src={brandIcon} width="30" height="30" className="inline align-text-bottom" />
          ThxNoThx
        </Link>
      </div>
      <PageContent>{children}</PageContent>
    </div>
  );
};

export const PageContent: React.FC = ({ children }) => {
  return (
    <main className="pb-8">
      <div className="max-w-7xl mx-auto sm:px-12 pt-4 sm:pt-12 h-full">
        <div className="px-4 sm:px-0 h-full">{children}</div>
      </div>
    </main>
  );
};

export const BasicPage: React.FC<Props> = ({ children, user }) => {
  const isSignedIn = !!user;
  return (
    <>
      <div className="min-h-full">
        <Disclosure as="nav" className="bg-white border-b border-gray-200">
          {({ open }) => (
            <>
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between h-16">
                  <div className="flex">
                    <LeftHandItems isSignedIn={isSignedIn} />
                  </div>
                  <RightHandItems isSignedIn={isSignedIn} />
                  <MobileMenuOpenButton menuOpen={open} />
                </div>
              </div>
              <MobileMenuPopUp user={user} />
            </>
          )}
        </Disclosure>
        <PageContent>{children}</PageContent>
      </div>
    </>
  );
};

const MobileMenuPopUp = ({ user }: { user: FullUser | undefined }) => {
  const isSignedIn = !!user;
  const menuItems = isSignedIn ? SignedInNavItems : SignedOutNavItems;
  return (
    <Disclosure.Panel className="sm:hidden">
      <div className="pt-2 pb-3">
        {menuItems.map((item) => (
          <Disclosure.Button
            key={item.name}
            as="a"
            href={item.href}
            className={classNames(
              item.current
                ? "bg-amber-50 border-amber-500 text-amber-700"
                : "border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800",
              "block pl-3 pr-4 py-2 border-l-4 text-base font-medium no-underline",
            )}
            aria-current={item.current ? "page" : undefined}
          >
            {item.name}
          </Disclosure.Button>
        ))}
      </div>
      {isSignedIn && (
        <div className="pt-2 pb-3 border-t border-gray-200">
          {/* <div className="flex items-center px-4">
         <div className="flex-shrink-0">
          <UserCircleIcon />
        </div>
        <div className="ml-3">
          <div className="text-base font-medium text-gray-800">{user?.username}</div>
          <div className="text-sm font-medium text-gray-500">{user?.email}</div>
        </div> 
      </div> */}
          <div className="mt-3 space-y-1">
            {userMenuItems.map((item) => (
              <Disclosure.Button
                key={item.name}
                as="a"
                href={item.href}
                className="block px-4 py-1 text-base font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100 no-underline"
              >
                {item.name}
              </Disclosure.Button>
            ))}
          </div>
        </div>
      )}
    </Disclosure.Panel>
  );
};
